import type { MutationFunctionOptions } from "@apollo/client";
import _ from "lodash";

import type { TRemoveVulnerabilityResult } from "./types";

import type {
  RemoveVulnerabilityJustification,
  RemoveVulnerabilityMutationMutation,
  RemoveVulnerabilityMutationMutationVariables,
} from "gql/graphql";

const removeVulnerabilitiesChunked = async (
  removeVulnerability: (
    options?: MutationFunctionOptions<
      RemoveVulnerabilityMutationMutation,
      RemoveVulnerabilityMutationMutationVariables
    >,
  ) => Promise<TRemoveVulnerabilityResult>,
  findingId: string,
  justification: RemoveVulnerabilityJustification,
  vulnerabilities: string[],
): Promise<TRemoveVulnerabilityResult[]> => {
  const chunkSize = 64;
  const vulnerabilitiesChunks = _.chunk(vulnerabilities, chunkSize);
  const closedChunks = vulnerabilitiesChunks.map(
    (chunk): (() => Promise<TRemoveVulnerabilityResult[]>) =>
      async (): Promise<TRemoveVulnerabilityResult[]> =>
        Promise.all(
          chunk.map(
            async (id): Promise<TRemoveVulnerabilityResult> =>
              removeVulnerability({
                update: (cache, result): void => {
                  if (result.data?.removeVulnerability.success === true) {
                    const normalizedId = cache.identify({
                      __typename: "Vulnerability",
                      id,
                    });
                    cache.evict({ id: normalizedId });
                    cache.gc();
                  }
                },
                variables: {
                  findingId,
                  id,
                  justification,
                },
              }),
          ),
        ),
  );

  return closedChunks.reduce(
    async (
      previousValue,
      currentValue,
    ): Promise<TRemoveVulnerabilityResult[]> => [
      ...(await previousValue),
      ...(await currentValue()),
    ],
    Promise.resolve<TRemoveVulnerabilityResult[]>([]),
  );
};

export { removeVulnerabilitiesChunked };
